import { Description, Field as FormField, FieldGroup, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { Link } from '../../catalyst/link'
import { dropNulls } from '../../lib/api'
import { ChoiceButtons } from '../chips'
import { fieldIdsOf, optNum, optText, str, type FlowDef } from '../flow'
import { rememberWho, WhoField } from '../who'
import { placeOf, TextField } from './shared'

// Field work with no compliance form (PRODUCT.md §6 Record): pruning, irrigation, mowing, mulching,
// pollination. Water with fertilizer or a cleaner in it is a crop-input application instead.

export const FIELD_TASKS = [
  { value: 'pruning', label: 'Pruning' },
  { value: 'irrigation', label: 'Irrigation' },
  { value: 'mowing_weed_control', label: 'Mowing / weed control' },
  { value: 'mulching', label: 'Mulching' },
  { value: 'pollination', label: 'Pollination (hives)' },
  { value: 'other', label: 'Other' },
] as const

const taskLabel = (t: string) => FIELD_TASKS.find((x) => x.value === t)?.label ?? t

/** Hours as typed ("1.5") to whole minutes for the record. */
const minutesOf = (hours: unknown) => {
  const h = optNum(hours)
  return h != null && h > 0 ? Math.round(h * 60) : undefined
}

export const fieldTaskFlow: FlowDef = {
  kind: 'field_task',
  label: 'Field work',
  needsField: true,
  severalFields: true,
  initial: ({ today, fieldId }) => ({ fieldId, date: today }),
  steps: [
    {
      id: 'work',
      title: 'What work?',
      valid: (v) => str(v.task) !== '' && (v.task !== 'other' || !!optText(v.otherTask)) && (!optText(v.hours) || minutesOf(v.hours) !== undefined),
      render: (ctx) => {
        const { values, patch, farmId } = ctx
        const carry = new URLSearchParams({ date: str(values.date), fields: fieldIdsOf(values).join(',') })
        return (
          <FieldGroup>
            <ChoiceButtons
              label="What did you do?"
              value={str(values.task) as (typeof FIELD_TASKS)[number]['value'] | ''}
              options={FIELD_TASKS}
              onChange={(t) => patch({ task: t })}
            />
            {values.task === 'other' && <TextField ctx={ctx} label="What was it?" valueKey="otherTask" />}
            {values.task === 'irrigation' && (
              <p className="rounded-lg bg-zinc-950/[0.03] p-3 text-sm/6 text-zinc-600 ring-1 ring-zinc-950/5 dark:bg-white/5 dark:text-zinc-400 dark:ring-white/10">
                Added fertilizer or a cleaner to the water? That is a crop-input application:{' '}
                <Link href={`/farms/${farmId}/log/crop_inputs?${carry}`}>log it as Applied crop inputs</Link>, method Drip / fertigation.
              </p>
            )}
            <FormField>
              <Label>How long? (hours, optional)</Label>
              <Description>On each field.</Description>
              <Input type="number" inputMode="decimal" min="0" step="any" value={str(values.hours)} onChange={(e) => patch({ hours: e.target.value })} />
            </FormField>
            <WhoField ctx={ctx} label="Who did it?" valueKey="performedBy" />
            <TextField ctx={ctx} label="Notes (optional)" valueKey="notes" multiline />
          </FieldGroup>
        )
      },
    },
  ],
  review: (ctx) => {
    const { values } = ctx
    return {
      title: `${taskLabel(str(values.task))}${placeOf(ctx)}`,
      lines: [
        { label: 'Date', value: str(values.date) },
        { label: 'Work', value: values.task === 'other' ? str(values.otherTask) : taskLabel(str(values.task)) },
        ...(minutesOf(values.hours) ? [{ label: 'How long', value: `${str(values.hours)} h on each field` }] : []),
        ...(optText(values.performedBy) ? [{ label: 'Done by', value: str(values.performedBy) }] : []),
        ...(optText(values.notes) ? [{ label: 'Notes', value: str(values.notes) }] : []),
      ],
    }
  },
  entry: ({ values }) =>
    dropNulls({
      fieldId: str(values.fieldId),
      doneOn: str(values.date),
      task: str(values.task),
      otherTask: values.task === 'other' ? (optText(values.otherTask) ?? null) : null,
      durationMinutes: minutesOf(values.hours) ?? null,
      performedBy: optText(values.performedBy) ?? null,
      notes: optText(values.notes) ?? null,
    }),
  historyPath: (farmId, fieldId) => `/farms/${farmId}/fields/${fieldId}/field-tasks`,
  onRecorded: rememberWho('performedBy'),
}
